/**
 * English admin UI strings — the source dictionary (ADR-043). Other languages
 * implement the same `Messages` shape. `{name}` placeholders are filled with
 * `fmt`/`interpolate`, `{ one, other }` pairs with `plural`.
 */
export const en = {
  common: {
    breadcrumb: 'Breadcrumb',
    loading: 'Loading',
    somethingWrong: 'Something went wrong',
    reference: 'Reference: {id}',
    dismiss: 'Dismiss',
    close: 'Close',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    remove: 'Remove',
    save: 'Save',
    saving: 'Saving…',
    create: 'Create',
    edit: 'Edit',
    search: 'Search',
    type: 'Type',
    status: 'Status',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    untitled: 'Untitled',
    previous: 'Previous',
    next: 'Next',
    total: '{count} total',
    range: '{from}–{to} of {total}',
    selected: '{count} selected',
    selectItem: 'Select {name}',
    name: 'Name',
    key: 'Key',
    description: 'Description',
    actions: 'Actions',
    updated: 'Updated',
    created: 'Created',
    version: 'Version',
    language: 'Language',
    languages: 'Languages',
    allLanguages: 'All languages',
    refresh: 'Refresh',
    back: 'Back',
    open: 'Open',
    none: 'None',
    email: 'E-mail',
    password: 'Password',
    displayName: 'Display name',
    enabled: 'Enabled',
    disabled: 'Disabled',
    details: 'Details',
    filter: 'Filter',
    clear: 'Clear',
    view: 'View',
  },
  list: {
    any: 'Any',
    filters: 'Filters',
    results: '{count} results',
    resultOne: '1 result',
    clear: 'Clear filters',
    removeFilter: 'Remove filter: {name}',
    since: 'since {day}',
    until: 'until {day}',
    min: 'Min',
    max: 'Max',
    from: 'From',
    to: 'To',
    range: '{first}–{last} of {total}',
    perPage: 'Per page',
    pages: 'Pages',
    noneMatch: 'Nothing matches the filters',
    noneMatchHelp: 'Change or clear the filters to see more.',
  },
  nav: {
    dashboard: 'Dashboard',
    content: 'Content',
    media: 'Media',
    contentTypes: 'Content types',
    plugins: 'Plugins',
    users: 'Users',
    roles: 'Roles',
    audit: 'Audit log',
    groupContent: 'Content',
    groupBuild: 'Build',
    groupAdmin: 'Administration',
    mail: 'E-mail',
    backup: 'Backup',
    website: 'Website',
  },
  crumbs: {
    content: 'Content',
    newEntry: 'New entry',
    edit: 'Edit',
    contentTypes: 'Content types',
    builder: 'Builder',
    media: 'Media',
    users: 'Users',
    roles: 'Roles',
    plugins: 'Plugins',
    details: 'Details',
    extension: 'Extension',
    audit: 'Audit log',
    settings: 'Settings',
    mail: 'E-mail',
    backup: 'Backup',
    website: 'Website',
  },
  mail: {
    kicker: 'Settings',
    title: 'E-mail',
    lead: 'One SMTP account for every e-mail of the CMS and its plugins. A plugin can use its own SMTP account instead.',
    cmsTitle: 'CMS SMTP',
    effective: 'In use: {source}',
    sourceDatabase: 'the settings below',
    sourceEnvironment: 'the server configuration (SMTP_HOST {host})',
    sourceLog: 'none — e-mails are only written to the log',
    emptyHelp:
      'Leave the host empty to use the server configuration (SMTP_* variables).',
    host: 'SMTP host',
    port: 'Port',
    security: 'Encryption',
    securityNone: 'STARTTLS / none',
    securityTls: 'SSL/TLS',
    username: 'User name',
    password: 'Password',
    passwordKeep: 'Saved — leave empty to keep it',
    removePassword: 'Remove the saved password',
    fromName: 'Sender name',
    fromAddress: 'Sender address',
    fromOptional: 'Empty = the CMS sender',
    replyTo: 'Reply-to address (optional)',
    save: 'Save',
    saved: 'E-mail settings saved',
    testTitle: 'Send a test e-mail',
    testTo: 'Recipient',
    testSend: 'Send test',
    testSent: 'Test e-mail sent ({account})',
    testLogged:
      'No SMTP configured: the test e-mail was only written to the log',
    testFailed: 'Sending failed: {error}',
    pluginsTitle: 'Plugin SMTP accounts',
    pluginsLead:
      'Plugins send through the CMS SMTP unless they have their own account here.',
    plugin: 'Plugin',
    account: 'Account',
    usesCms: 'CMS SMTP',
    ownAccount: 'Own: {host}',
    ownDisabled: 'Own (off): {host}',
    configure: 'Own SMTP',
    edit: 'Edit',
    remove: 'Remove',
    removeConfirm:
      'Remove the SMTP account of {plugin}? It will send through the CMS SMTP again.',
    removed: 'Plugin SMTP account removed',
    pluginDialog: 'SMTP account for {plugin}',
    enabled: 'Use this account for the plugin’s e-mails',
    noPlugins: 'No plugins installed',
    deliveriesTitle: 'Recent e-mails',
    deliveriesEmpty: 'No e-mails sent yet',
    time: 'Time',
    source: 'Sender',
    to: 'To',
    subject: 'Subject',
    status: 'Status',
    statusSENT: 'Sent',
    statusFAILED: 'Failed',
    statusLOGGED: 'Logged only',
    accountplugin: 'plugin SMTP',
    accountcms: 'CMS SMTP',
    accountenvironment: 'server SMTP',
    accountlog: 'log',
    deliveriesSearch: 'Recipient or subject',
  },
  notifications: {
    title: 'Notifications',
    unseen: '{count} new',
    empty: 'No notifications yet.',
    markAllRead: 'Mark all as read',
    dismiss: 'Dismiss',
    soundOn: 'Turn the notification sound on',
    soundOff: 'Turn the notification sound off',
    sources: {
      content: 'Content',
      admin: 'Administrator',
      identity: 'Accounts',
      media: 'Media',
      plugins: 'Plugins',
    },
  },
  shell: {
    mainNavigation: 'Main navigation',
    home: 'GX-CMS home',
    noRole: 'No role',
    lightTheme: 'Switch to light theme',
    darkTheme: 'Switch to dark theme',
    signOut: 'Sign out',
    openNavigation: 'Open navigation',
    filterMenu: 'Filter menu',
    noMenuMatches: 'No matching pages',
    expandAll: 'Expand all groups',
    collapseAll: 'Collapse all groups',
    resizeNavigation:
      'Resize the navigation: drag, or use the arrow keys; double-click or Enter resets',
    collapseNavigation: 'Collapse navigation to icons',
    expandNavigation: 'Expand navigation',
    language: 'Language',
    accessDenied: 'Access denied',
    accessDeniedText:
      'You don’t have permission to view this page. Ask an administrator for access.',
    notFound: 'Page not found',
    notFoundText: 'The page you are looking for does not exist.',
  },
  fields: {
    invalidJson: 'Invalid JSON',
    missingFile: 'Missing file',
    addMedia: 'Add media',
    chooseMedia: 'Choose media',
    missingEntry: 'Missing entry',
    unlink: 'Unlink',
    linkMore: 'Link more',
    linkEntry: 'Link entry',
    removeItem: 'Remove item',
    addItem: 'Add item',
    linkEntries: 'Link entries',
    link: 'Link',
    allAllowedTypes: 'All allowed types',
    noMatchingEntries: 'No matching entries',
  },
  confirm: {
    stillInUse: 'Still in use',
    andMore: '…and {count} more',
    deleteAnyway: 'Delete anyway',
    typeToConfirm: 'Type {text} to confirm',
  },
  actor: {
    system: 'System',
    you: 'You',
    service: 'Service · {name}',
    plugin: 'Plugin · {name}',
    user: 'User {id}',
  },
  status: {
    PUBLISHED: 'published',
    ACTIVE: 'active',
    ENABLED: 'enabled',
    READY: 'ready',
    SUCCESS: 'success',
    HEALTHY: 'healthy',
    DRAFT: 'draft',
    INSTALLED: 'installed',
    IN_REVIEW: 'in review',
    SCHEDULED: 'scheduled',
    PROCESSING: 'processing',
    UPLOADING: 'uploading',
    INVITED: 'invited',
    DISCOVERED: 'discovered',
    ARCHIVED: 'archived',
    DISABLED: 'disabled',
    DELETED: 'deleted',
    UNKNOWN: 'unknown',
    FAILED: 'failed',
    FAILURE: 'failure',
    LOCKED: 'locked',
    UNHEALTHY: 'unhealthy',
    PENDING: 'pending',
  } as Record<string, string>,
  auth: {
    hero: 'Content, {em}',
    heroEm: 'under control.',
    sub: 'GX-CMS — structured content, revisions and publishing for every channel.',
    welcome: 'Welcome back',
    signIn: 'Sign in',
    failed: 'Sign-in failed',
  },
  dashboard: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    lead: 'What’s happening across your content.',
    newEntry: 'New entry',
    entries: 'Entries',
    published: 'Published',
    drafts: 'Drafts',
    inReview: 'In review',
    scheduled: 'Scheduled',
    recent: 'Recently edited',
    viewAll: 'View all',
    noContent: 'No content yet',
    noContentText: 'Create your first entry to see it here.',
    awaitingReview: 'Awaiting review',
    nothingReview: 'Nothing waiting for review.',
    noScheduled: 'No scheduled publications.',
    fromPlugins: 'From your plugins',
    byLanguage: 'Entries by language',
  },
  entries: {
    drafts: 'Drafts',
    inReview: 'In review',
    scheduled: 'Scheduled',
    published: 'Published',
    archived: 'Archived',
    liveIn: 'Live in {language}',
    draftIn: 'Draft in {language} (not published in it)',
    createEntry: 'Create entry',
    noActiveTypes: 'No active content types',
    noActiveTypesText: 'Activate a content type before creating entries.',
    deleteCount: {
      one: 'Delete {count} entry?',
      other: 'Delete {count} entries?',
    },
    deleteText:
      'Selected entries and their version history are permanently deleted. Published pages disappear from the site. This cannot be undone.',
    stillReferenced: 'Some entries are still referenced',
    referencedText: {
      one: '{count} entry is linked from other entries. Delete anyway and remove those links?',
      other:
        '{count} entries are linked from other entries. Delete anyway and remove those links?',
    },
    deleted: { one: '{count} entry deleted', other: '{count} entries deleted' },
    deleteFailed: {
      one: '{count} entry could not be deleted.',
      other: '{count} entries could not be deleted.',
    },
    allEntries: 'All entries',
    lead: 'Drafts, reviews, scheduled and published content across every type.',
    deleteN: 'Delete {count}',
    searchPlaceholder: 'Title or slug',
    contentType: 'Content type',
    allTypes: 'All types',
    nothingMatches: 'Nothing matches',
    noEntries: 'No entries yet',
    createAn: 'Create an entry',
    tryFilter: 'Try a different filter.',
    noEntriesText: 'Entries you create appear here with their workflow status.',
    selectAll: 'Select all deletable entries on this page',
    title: 'Title',
    live: 'Live',
    onlyOwn: 'You can only delete your own entries',
    changes: 'Changes',
  },
  editor: {
    scheduleTitle: 'Schedule publication',
    schedule: 'Schedule',
    scheduleText:
      'The current draft version will be published automatically at the chosen time. Editing the entry afterwards does not change what gets published.',
    publishAt: 'Publish at',
    restoredPublished: 'Version {n} restored and published',
    restoredDraft: 'Version {n} restored as a new draft',
    versionN: 'Version {n}',
    restorePublish: 'Restore & publish',
    restoreDraft: 'Restore as draft',
    summary: 'Summary',
    schema: 'Schema',
    restoreNote:
      'Restoring creates a new version from this snapshot. History is never rewritten.',
    removeLanguageTitle: 'Remove {language}?',
    removeLanguageText:
      'Its translations are removed from the entry when you save, and the page is no longer delivered in this language after the next publish. Earlier versions keep them.',
    discardTitle: 'Discard changes?',
    discardText: 'You have unsaved changes. Leave without saving?',
    discard: 'Discard',
    created: 'Entry created',
    savedAs: 'Saved as version {n}',
    conflict: 'Someone else saved this entry. Reload to see their changes.',
    chooseType: 'Choose a content type to create an entry.',
    createDraft: 'Create draft',
    saveDraft: 'Save draft',
    saved: 'Saved',
    fixFields: 'Please fix the highlighted fields',
    actionFailed: 'The action could not be completed',
    editingLanguage: 'Editing language',
    contentLanguage: 'Content language',
    languagesHelp:
      'The languages this entry exists in. The website shows it only in these languages; publishing needs every required field in each of them.',
    publishing: 'Publishing',
    liveChanges: 'Live · changes pending',
    draft: 'Draft',
    notPublished: 'Not published',
    publishedToast: 'Published',
    savePublish: 'Save & publish',
    republish: 'Republish',
    publish: 'Publish',
    submittedToast: 'Submitted for review',
    submit: 'Submit for review',
    returnedToast: 'Returned to draft',
    returnToDraft: 'Return to draft',
    saveFirst: 'Save first',
    scheduleCancelled: 'Schedule cancelled',
    cancelSchedule: 'Cancel schedule',
    unpublishTitle: 'Unpublish entry?',
    unpublishText:
      'It will disappear from the delivery API immediately. The draft is kept.',
    unpublish: 'Unpublish',
    unpublishedToast: 'Unpublished',
    preview: 'Preview',
    archiveTitle: 'Archive entry?',
    archiveText:
      'Archiving unpublishes the entry and hides it from editors’ default views.',
    archive: 'Archive',
    archivedToast: 'Archived',
    restoredToast: 'Restored',
    restore: 'Restore',
    deleteTitle: 'Delete entry?',
    deleteText:
      '“{title}” and its whole version history will be permanently deleted.',
    deletePublic: 'Its public page disappears from the site.',
    cannotUndo: 'This cannot be undone.',
    deleted: 'Entry deleted',
    slug: 'Slug',
    slugGenerated: 'Generated from title',
    slugHelp: 'Used in URLs and the delivery API.',
    fromTitle: 'From title',
    changeNote: 'Change note',
    changeNotePlaceholder: 'What changed? (optional)',
    liveUrl: 'Live URL',
    noPublicPage:
      'No public page (missing slug or category, or the type is not public)',
    history: 'History',
    liveLower: 'live',
    draftSaved: 'Draft saved',
    noHistory: 'No history yet.',
    scheduledFor: 'Scheduled for {time}',
  },
  plugins: {
    plugin: 'Plugin',
    continue: 'Continue',
    frameNoValues: 'This extension cannot change field values.',
    frameAdminPaths: 'Only admin paths can be opened.',
    frameNoMedia: "This plugin has not been granted 'media.asset.read'.",
    requestFailed: 'Request failed',
    isolated: 'Rendered by a plugin in an isolated frame',
    extensionUnavailable: 'Extension unavailable',
    extensionUnavailableText:
      'This plugin page is not registered, or the plugin is disabled.',
  },
  types: {
    titleField: 'Title',
    createdToast: 'Content type “{name}” created',
    newType: 'New content type',
    namePlaceholder: 'Article',
    apiKey: 'API key',
    apiKeyHelp:
      'Used in API paths, e.g. /api/v1/delivery/article. Cannot change later.',
    titleAdded: 'A required “Title” field is added to get you started.',
    kicker: 'Model your content',
    lead: 'Define the structure of your content. Entries are validated against these schemas on every save and publish.',
    none: 'No content types yet',
    createFirst: 'Create your first type',
    noneText:
      'Content types describe what an article, a page or a product looks like.',
    fields: 'Fields',
    one: 'Content type',
    searchPlaceholder: 'Name, API key or description',
  },
  fieldTypes: {
    TEXT: {
      label: 'Text',
      description: 'Short plain text such as titles or names',
    },
    RICH_TEXT: {
      label: 'Rich text',
      description: 'Long formatted text (Markdown)',
    },
    NUMBER: {
      label: 'Number',
      description: 'Integers or decimals with optional bounds',
    },
    BOOLEAN: { label: 'Boolean', description: 'Yes / no toggle' },
    DATE: { label: 'Date', description: 'Calendar date' },
    DATETIME: { label: 'Date & time', description: 'Date with time of day' },
    JSON: { label: 'JSON', description: 'Arbitrary structured data' },
    MEDIA: {
      label: 'Media',
      description: 'Images, video or documents from the library',
    },
    RELATION: { label: 'Relation', description: 'References to other entries' },
    ENUM: { label: 'Choice', description: 'One value from a fixed list' },
    URL: { label: 'URL', description: 'Web address' },
  },
  builder: {
    required: 'required',
    localized: 'translated',
    list: 'list',
    images: 'Images',
    video: 'Video',
    audio: 'Audio',
    defaultInvalid:
      'Default value must be valid JSON (e.g. "text", 42, true or ["a"]).',
    editField: 'Edit field',
    addField: 'Add field',
    saveField: 'Save field',
    locked:
      'Entries exist for this type. The key, list and localization settings are locked; only compatible type changes are allowed.',
    label: 'Label',
    keyHelp: 'Property name in the API payload.',
    requiredLabel: 'Required',
    localizedLabel: 'Translated (one value per language)',
    multiple: 'Multiple values',
    helpText: 'Help text',
    helpTextHelp: 'Shown to editors under the field.',
    validation: 'Validation',
    minLength: 'Min length',
    maxLength: 'Max length',
    minimum: 'Minimum',
    maximum: 'Maximum',
    pattern: 'Pattern (regular expression)',
    integer: 'Whole numbers only',
    minItems: 'Min items',
    maxItems: 'Max items',
    options: 'Options',
    optionsHelp: 'One option per line.',
    allowedTypes: 'Allowed content types',
    anyType: 'Leave empty to allow any type.',
    allowedMedia: 'Allowed media',
    anyMedia: 'Leave empty to allow any media type.',
    defaultValue: 'Default value (JSON)',
    defaultHelp: 'Applied to new entries. Examples: "Untitled", 0, true.',
    settingsSaved: 'Settings saved',
    keysPermanent: 'Keys are permanent.',
    titleField: 'Title field',
    firstText: 'First text field',
    titleFieldHelp: 'Shown as the entry name in lists.',
    slugFrom: 'Generate slug from',
    noGenerate: 'Do not generate',
    slugRequired: 'Slug required to publish',
    previewTemplate: 'Preview URL template',
    previewHelp:
      'Placeholders: {slug}, {id}, {token}. Used by the “Preview” action.',
    publicUrls: 'Public URLs',
    reserved:
      'The prefix {prefix} is reserved by {owner}. Changing it moves these pages out of the plugin’s URL space.',
    urlPrefix: 'URL prefix',
    urlPrefixHelp:
      'First URL segment for this type. Must be unique; system words and locale codes are reserved.',
    categoryFrom: 'Category segment from',
    noCategory: 'No category segment',
    choice: 'choice',
    linkedSlug: 'linked entry slug',
    categoryHelp: 'A single choice field or a link to a category entry.',
    publicPages: 'Entries have public pages',
    servedAt:
      'Pages are served at {path}, in each language the entry exists in. Entries without a slug get no public page. Changing the prefix keeps old URLs working with permanent (308) redirects.',
    servedAtCategory:
      'Pages are served at {path}, in each language the entry exists in. Entries without a slug or category get no public page. Changing the prefix or category keeps old URLs working with permanent (308) redirects.',
    widgets: 'Field widgets (plugins)',
    defaultEditor: 'Default editor',
    saveSettings: 'Save settings',
    fieldSaved: 'Field saved',
    viewEntries: 'View entries',
    activated: 'Content type activated',
    activate: 'Activate',
    archiveTitle: 'Archive content type?',
    archiveText:
      'No new entries can be created and delivery stops for this type. Existing entries are kept.',
    deleteTitle: 'Delete content type?',
    deleteWithEntries: {
      one: '“{name}” and its {count} entry (every version and language) will be permanently deleted. Published pages disappear from the site. This cannot be undone.',
      other:
        '“{name}” and all of its {count} entries (every version and language) will be permanently deleted. Published pages disappear from the site. This cannot be undone.',
    },
    deleteEmpty: '“{name}” will be permanently deleted. This cannot be undone.',
    deleted: 'Content type deleted',
    schemaVersion: 'Schema version {n}',
    noFields: 'No fields',
    noFieldsText: 'Add fields to describe this content type.',
    titleTag: 'TITLE',
    widget: 'widget {name}',
    moveUp: 'Move up',
    moveDown: 'Move down',
    editX: 'Edit {name}',
    removeX: 'Remove {name}',
    removeFieldTitle: 'Remove field?',
    removeFieldUsed:
      '“{name}” disappears from the editor and delivery. Values remain in older revisions.',
    removeFieldEmpty: '“{name}” will be removed.',
    fieldRemoved: 'Field removed',
    namesTitle: 'Name on the website',
    namesHelp:
      'How this type is called in each language (navigation, labels). Empty: the name above.',
  },
  media: {
    saved: 'Media details saved',
    details: 'Media details',
    deleteTitle: 'Delete file?',
    deleteText:
      '“{name}” is removed from storage and stops loading everywhere. This cannot be undone.',
    deleted: 'File deleted',
    file: 'File',
    size: 'Size',
    uploaded: 'Uploaded',
    checksum: 'Checksum',
    altText: 'Alt text',
    altHelp:
      'Describe the image for screen readers. Required for accessible content.',
    visibility: 'Visibility',
    public: 'Public — stable URL, cacheable',
    private: 'Private — short-lived signed links only',
    linkCopied: 'Link copied',
    copyLink: 'Copy link',
    download: 'Download',
    kicker: 'Library',
    lead: 'Images, video, audio and documents. Files are stored in object storage and verified by their signature.',
    searchPlaceholder: 'File name, title or alt text',
    documents: 'Documents',
    none: 'No media yet',
    noneText: 'Upload files to use them in your content.',
    openX: 'Open {name}',
    missingAlt: 'Missing alt text',
    noAlt: 'NO ALT',
    selectMedia: 'Select media',
    selectFile: 'Select a file',
    useSelected: 'Use selected',
    pickerSearch: 'Search by file name or alt text',
    searchMedia: 'Search media',
    noneFound: 'No media found',
    uploadedX: 'Uploaded {name}',
    drop: '{strong} or click to browse',
    dropStrong: 'Drop files here',
    dropHelp: 'Images, video, audio and PDF. File type is verified on upload.',
  },
  pluginList: {
    kicker: 'Extend',
    lead: 'Plugins are untrusted until an administrator reviews their capabilities, installs and enables them. They run isolated from the CMS core.',
    invalid: '{count} package(s) failed validation: {paths}',
    scanned: 'Registry scanned: {count} package(s)',
    scan: 'Scan registry',
    none: 'No plugins found',
    noneText:
      'Place plugin packages (with a cms-plugin.json) in the plugin registry directory, then scan the registry.',
    capabilities: 'Capabilities',
    health: 'Health',
    update: 'Update: {version}',
    signed: 'Signed',
    badSignature: 'Bad signature',
    packageMissing: 'Package missing',
    granted: '{granted} / {requested} granted',
    noSettings: 'This plugin has no settings.',
    saveConfig: 'Save configuration',
    updates: 'Update available',
    searchPlaceholder: 'Name, key or publisher',
  },
  pluginDetail: {
    features: 'Features',
    featuresText:
      'Feature flags turn parts of the plugin on or off for everyone. They take effect only while the plugin is enabled and never grant permissions.',
    installFirst: 'Install the plugin to manage its features.',
    turnedOn: '{key} turned on',
    turnedOff: '{key} turned off',
    inactive: 'inactive while the plugin is disabled',
    changed: 'changed {time}',
    installed: '{name} installed',
    updatedTo: '{name} updated to {version}',
    installX: 'Install {name}',
    updateX: 'Update {name}',
    approveInstall: 'Approve & install',
    approveUpdate: 'Approve & update',
    installFailed: 'The plugin could not be installed',
    unsigned:
      'This package is not signed by a trusted publisher. Only install plugins whose source you have reviewed.',
    requests:
      '{name} {version} requests the capabilities below. Uncheck anything you do not want to grant — the plugin can only use what you approve, and you can revoke grants at any time.',
    granted: 'Granted',
    migrationsWillRun:
      '{count} migration(s) will run in the isolated plugin runtime and be recorded in the audit log.',
    uninstallX: 'Uninstall {name}',
    uninstalled: 'Plugin uninstalled',
    uninstall: 'Uninstall',
    uninstallText:
      'All capability grants are revoked. Content the plugin created through the CMS API (for example content types) is not removed.',
    pluginData: 'Plugin data',
    keepData: 'Keep plugin data and configuration (reinstall later)',
    deleteData: 'Delete plugin data and configuration permanently',
    typeKey: 'Type {key} to confirm',
    disableX: 'Disable {name}',
    disabledToast: 'Plugin disabled',
    disable: 'Disable',
    disableText:
      'Event subscriptions, jobs and admin extensions stop immediately. Plugin data is kept.',
    reason: 'Reason (optional)',
    incident: 'Security incident — block automatic re-enablement',
    install: 'Install',
    updateTo: 'Update to {version}',
    enabledToast: '{name} enabled',
    enable: 'Enable',
    deleteTitle: 'Delete plugin record?',
    deleteGone:
      'The record of “{name}” (its package is gone) and any retained plugin data are permanently deleted.',
    deleteRegistry:
      'The record of “{name}” and any retained plugin data are permanently deleted. Its package is still in the registry, so it reappears as discovered on the next registry scan.',
    deleted: 'Plugin deleted',
    lastError: 'Last error',
    notCompatible: 'Not compatible',
    overview: 'Overview',
    configuration: 'Configuration',
    migrations: 'Migrations',
    package: 'Package',
    installedLabel: 'Installed',
    notInstalled: 'Not installed',
    available: 'Available',
    publisher: 'Publisher',
    license: 'License',
    defaultLanguage: '{language} (default)',
    languagesHelp:
      'The plugin’s own screens use the admin language when it is listed here, otherwise its default language.',
    signature: 'Signature',
    integrity: 'Integrity',
    homepage: 'Homepage',
    backendService: 'Backend service',
    bound: 'bound',
    notBound: 'not bound',
    permissionsAdded: 'Permissions it adds',
    defaultRoles: 'default: administrator, {roles}',
    runtime: 'Runtime',
    lastCheck: 'Last check',
    backend: 'Backend',
    adminUi: 'Admin UI',
    events: 'Events',
    jobs: 'Jobs',
    every: '{id} (every {seconds}s)',
    dependencies: 'Dependencies',
    optional: 'optional',
    notInstalledLower: 'not installed',
    missing: 'missing',
    grants: 'Capability grants',
    grantsText:
      'Deny by default: a requested capability does nothing until granted. Revoking takes effect within seconds, also for running plugin processes.',
    grantedAt: 'Granted {time}',
    noLongerRequested: 'No longer requested',
    notGranted: 'Not granted',
    revokedToast: 'Capability revoked',
    grantedToast: 'Capability granted',
    revoke: 'Revoke',
    grant: 'Grant',
    configSaved: 'Configuration saved',
    migrationsDone: 'Migrations completed',
    retry: 'Retry',
    noMigrations: 'No migrations declared.',
    migration: 'Migration',
    applied: 'Applied',
  },
  users: {
    permissionsCount: '{count} permissions',
    created: 'User created',
    invite: 'Invite a user',
    createUser: 'Create user',
    initialPassword: 'Initial password',
    passwordHelp:
      'At least 12 characters with three of: lowercase, uppercase, digit, symbol. Share it securely.',
    updated: 'User updated',
    deleteTitle: 'Delete user?',
    deleteText:
      '{name} ({email}) is signed out everywhere and permanently removed with all sessions and role assignments. Content they created is kept. This cannot be undone.',
    deleted: 'User deleted',
    disableTitle: 'Disable user?',
    disableText:
      '{name} is signed out everywhere and cannot sign in until re-enabled.',
    reenabled: 'User re-enabled',
    unlock: 'Unlock',
    lastSignIn: 'Last sign-in',
    resetPassword: 'Reset password',
    newPassword: 'New password',
    resetDone: 'Password reset; all sessions signed out',
    reset: 'Reset',
    lead: 'People who can sign in to GX-CMS. Access is granted through roles.',
    add: 'Add user',
    searchPlaceholder: 'Name or email',
    anyStatus: 'Any status',
    none: 'No users found',
  },
  roles: {
    audit: 'Audit',
    newRole: 'New role',
    lead: 'Roles bundle permissions. Every API checks permissions server-side on each request; changes apply within seconds.',
    users: { one: '{count} user', other: '{count} users' },
    system: 'System',
    deleteTitle: 'Delete role?',
    deleteText: '{count} user(s) lose the permissions of “{name}”.',
    saved: 'Permissions saved',
    adminAll: 'The administrator role always holds every permission.',
    created: 'Role created — now choose its permissions',
  },
  permissions: {
    'user.read': 'View users and roles',
    'user.manage': 'Create, update and disable users; manage roles',
    'user.delete': 'Permanently delete user accounts',
    'content.type.read': 'View content types and fields',
    'content.type.manage': 'Create and modify content types and fields',
    'content.entry.read': 'View content entries, drafts and revisions',
    'content.entry.create': 'Create content entries',
    'content.entry.update':
      'Edit drafts, submit for review and archive entries',
    'content.entry.publish': 'Publish, unpublish and schedule entries',
    'content.entry.rollback': 'Restore entries to previous revisions',
    'content.entry.delete': 'Permanently delete any content entry',
    'content.entry.delete.own':
      'Permanently delete content entries you created',
    'content.type.delete':
      'Delete content types, including all of their entries',
    'media.read': 'View the media library',
    'media.upload': 'Upload and edit media assets',
    'media.delete': 'Delete any media asset',
    'media.delete.own': 'Delete media assets you uploaded',
    'plugin.read': 'View plugins',
    'plugin.install':
      'Discover, install and uninstall plugins; grant capabilities',
    'plugin.enable': 'Enable and disable plugins',
    'plugin.configure': 'Configure plugins',
    'plugin.delete': 'Remove plugin records from the registry',
    'plugin.feature.manage': 'Turn plugin feature flags on and off',
    'audit.read': 'Search the audit log',
    'mail.settings.manage':
      'Manage the e-mail (SMTP) settings of the CMS and plugins',
    'mail.send': 'Send e-mail through the CMS mail settings (plugins)',
    'backup.export':
      'Download a backup of all data and media, including users and settings',
    'backup.import': 'Replace all data and media with a backup',
    'site.settings.manage':
      "Edit the website's name, description, languages and contact details",
  } as Record<string, string>,
  audit: {
    lead: 'An append-only record of security- and business-relevant events across all services.',
    action: 'Action',
    actionPlaceholder: 'e.g. content.entry.published',
    resource: 'Resource',
    any: 'Any',
    outcome: 'Outcome',
    from: 'From',
    to: 'To',
    none: 'No events',
    noneText: 'Nothing matches these filters.',
    time: 'Time',
    actor: 'Actor',
    event: 'Audit event',
    source: 'Source',
    correlation: 'Correlation',
    eventId: 'Event ID',
    metadata: 'Metadata',
  },
  settings: {
    profileUpdated: 'Profile updated',
    mismatch: 'The new passwords do not match.',
    passwordChanged: 'Password changed. Other sessions were signed out.',
    kicker: 'Your account',
    profile: 'Profile',
    emailHelp: 'Ask an administrator to change your email.',
    saveProfile: 'Save profile',
    currentPassword: 'Current password',
    passwordHelp:
      'At least 12 characters with three of: lowercase, uppercase, digit, symbol.',
    confirmPassword: 'Confirm new password',
    changePassword: 'Change password',
    appearance: 'Appearance',
    design: 'Theme',
    designHelp:
      'Changes the look of the admin and the plugin screens for you on this browser. Light or dark is chosen below.',
    designs: {
      gxlabs: {
        name: 'GXLabs (default)',
        about: 'Black and white with a cyan accent',
      },
      apple: { name: 'Apple', about: 'Calm, rounded, macOS-style' },
      aurora: { name: 'Aurora', about: 'Violet, fuchsia and cyan' },
      sunset: { name: 'Sunset', about: 'Coral, tangerine and rose' },
      lagoon: { name: 'Lagoon', about: 'Teal, emerald and sky' },
      neon: { name: 'Neon', about: 'Magenta and cyan, best in dark mode' },
    },
    theme: 'Mode',
    light: 'Light',
    dark: 'Dark',
    system: 'Match system',
    languageHelp:
      'The admin is available in the site languages. Plugin screens that do not support your language use their default language.',
    access: 'Access',
    permissions: 'Permissions',
  },
  website: {
    kicker: 'Administration',
    title: 'Website',
    lead: "What the public website shows about itself: its name, description, languages and contact details. The CMS admin doesn't use these settings.",
    general: 'General',
    name: 'Website name',
    nameHelp: 'In the browser tab, in search results and in the footer.',
    descriptionIn: 'Description ({language})',
    descriptionHelp:
      'Shown in search results and when a page of the website is shared.',
    languages: 'Languages',
    languagesLabel: 'Languages of the website',
    languagesHelp:
      'The website offers only these. Editors can still write content in every CMS language: it appears on the website once its language is turned on here.',
    defaultLanguage: 'Default language',
    defaultLanguageHelp:
      'Visitors who open the address without a language land here.',
    contact: 'Contact',
    contactLead:
      'In the contact band and the footer of every page. Empty fields are hidden.',
    email: 'E-mail',
    phone: 'Phone',
    address: 'Address',
    mapQuery: 'Map',
    mapQueryHelp:
      'Google Maps search text of the map at the bottom of every page, e.g. the company name and address. Empty = no map.',
    social: 'Social media',
    fonts: 'Fonts',
    adobeFontsKit: 'Adobe Fonts kit ID',
    adobeFontsKitHelp:
      "The kit with the website's fonts (allow the website's domains in Adobe Fonts). Empty = free fallback fonts.",
    save: 'Save',
    saved: 'Website settings saved. The website shows them within a minute.',
    lastSaved: 'Last saved {when}',
    neverSaved: 'Not saved yet.',
  },
  backup: {
    kicker: 'Administration',
    title: 'Backup',
    lead: 'Download everything this site holds as one ZIP file, or replace it with such a file: to keep a copy, or to move the site to another server.',
    exportTitle: 'Download a backup',
    exportLead:
      'The ZIP file holds the data of every part of the CMS (content, users and roles, settings, plugins, CRM, shop, bookings, messages, audit log) as SQL, and every media file.',
    exportSecret:
      'It contains personal data, password hashes and encrypted secrets. Store it like a password.',
    exportKeys:
      'Secrets saved in the admin (SMTP passwords, payment keys) are encrypted with the server keys PLUGIN_SECRETS_KEY and MAIL_SECRETS_KEY. They stay readable only where the same keys are set.',
    download: 'Download backup',
    preparing: 'Preparing…',
    downloadStarted: 'The download has started. Large sites take a while.',
    importTitle: 'Import a backup',
    importLead:
      'Upload a backup made by GX-CMS. You will see what it contains before anything changes.',
    importReplaces:
      'An import replaces all data and media files of this site. Users, passwords and roles come from the backup, so you sign in with an account from the backup afterwards.',
    chooseFile: 'Choose a ZIP file',
    uploading: 'Uploading… {percent}%',
    checking: 'Checking the backup…',
    backupOf: 'Backup of {source}',
    unknownSource: 'unknown site',
    createdAt: 'Made on {date} with GX-CMS {version}',
    part: 'Part',
    rows: 'Records',
    mediaFiles: 'Media files',
    mediaSummary: '{files} files, {size}',
    canImport: 'This backup can be imported.',
    cannotImport: 'This backup cannot be imported here:',
    notes: 'Good to know:',
    replace: 'Replace all data…',
    discard: 'Discard upload',
    discarded: 'Upload discarded',
    confirmTitle: 'Replace all data?',
    confirmMessage:
      'All content, users, settings and plugin data of this site are replaced with the backup from {date}. A copy of the current data is kept until the import has finished and is restored if the import fails. Type {word} to continue.',
    confirmWord: 'REPLACE',
    confirmLabel: 'Replace all data',
    running: 'Importing…',
    steps: {
      check: 'Checking',
      media: 'Copying media files ({count})',
      'safety-copy': 'Keeping a copy of the current data',
      database: 'Loading {part}',
      rollback: 'Something failed: restoring the previous data',
      done: 'Done',
    } as Record<string, string>,
    succeeded:
      'The backup was imported: {rows} records and {files} media files. Sign in with an account from the backup.',
    signIn: 'Sign in',
    failed: 'The import failed: {error}',
    rolledBack:
      'The previous data was restored; nothing changed except new media files.',
    notRolledBack:
      'The data may be incomplete. Import the backup again, or another one.',
    statusLost:
      'You were signed out while the import runs. Its progress is shown below.',
    parts: {
      'identity-service': 'Users, roles and e-mail settings',
      'content-service': 'Content and content types',
      'media-service': 'Media library',
      'plugin-service': 'Plugins and their settings',
      'audit-service': 'Audit log',
      'crm-service': 'CRM',
      'webshop-service': 'Webshop',
      'appointments-service': 'Appointment booking',
      'contact-service': 'Contact messages',
      'mobile-app-service': 'Mobile app',
    } as Record<string, string>,
    issues: {
      format_unsupported:
        'It was made by a newer GX-CMS (backup format {version}).',
      manifest_invalid: 'It is not a GX-CMS backup, or the file is damaged.',
      file_missing: 'A part of the backup is missing ({part}).',
      service_unavailable:
        '{part}: this part does not run on this site. Turn it on first.',
      service_not_in_backup:
        '{part}: not in the backup; its current data stays.',
      migrations_differ_older:
        '{part}: the backup was made by an older version of GX-CMS. Import it into that version, then update.',
      migrations_differ_newer:
        '{part}: the backup was made by a newer version of GX-CMS. Update this site first.',
      migrations_differ_different:
        '{part}: the backup was made by a different version of GX-CMS.',
      key_differs:
        '{part}: this server has another {key}. Secrets saved in the admin (passwords, payment keys) cannot be read after the import; enter them again, or set the same key first.',
    } as Record<string, string>,
  },
  errors: {
    generic: 'Something went wrong.',
    offline: 'Cannot reach GX-CMS. Check your connection.',
    uploadFailed: 'Upload failed',
    uploadNetwork: 'Upload failed: network error.',
  },
}
